/// <amd-module name="VSS/Redux/RootProvider" />
import * as React from "react";
import { Provider } from "react-redux";
import { DeepPartial } from "redux";
import { createStore, IModule, IModuleStore } from "redux-dynamic-modules";
import { getSagaExtension } from "redux-dynamic-modules-saga";
import { getContentViewObserverModule } from "VSS/Redux/Modules/ContentViewObserver";
import { NavigationModule } from "VSS/Redux/Modules/Navigation";
import { QueuedActionsModule } from "VSS/Redux/Modules/QueuedActions";
import { getTelemetryModule } from "VSS/Redux/Modules/Telemetry";
import { getTTIMonitorModule, PostTTI } from "VSS/Redux/Modules/TTIMonitor";
import { VSSPageDataModule } from "VSS/Redux/Modules/VSSPageData";
import { IContentViewObserverModuleArgs } from "./Modules/ContentViewObserver/ContentViewObserverContracts";
import { WrappedModule } from "./Modules";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IReduxStoreService } from "./StoreService";

export interface IPostTTIModule {
    /**
     * Id of the webproj contribution that includes the module
     */
    contributionId: string;

    /**
     * Id of the redux module in the registration
     */
    reduxModuleId: string;
}

export interface IRootProviderProps<SagaContext, State> {
    /**
     * Initial state for the store
     */
    initialState: DeepPartial<State>;

    /**
     * Context for the sagas
     */
    sagaContext: SagaContext;

    /**
     * Scenario name to used for telemetry
     */
    scenarioName: string;

    /**
     * Initial set of modules to load in the store
     */
    modules: IModule<any>[];

    /**
     * Modules to load after TTI has been marked
     */
    postTTIModules?: IPostTTIModule[];

    /**
     * Split timing name to use for TTI after loading the store
     */
    ttiSplitTimingName?: string;

    /**
     * Arguments for ContentViewObserver module
     */
    contentViewObserverModuleArgs?: IContentViewObserverModuleArgs;

    /**
     * Puts redux store in store service
     */
    useStoreService?: boolean;

    /**
     * The GUID of the service instance type to use for telemetry modules
     */
    serviceInstanceType?: string;
}

/**
 * Should be used as the root reducer
 * This loads some common modules e.g. telemetry, tti, hub view state
 * Also disposes the store automatically on unmount
 */
export class RootProvider<SagaContext, State> extends VssComponent<IRootProviderProps<SagaContext, State>> {
    private _store?: IModuleStore<any>;
    private _caughtSagaError: Error;
    private _mounted: boolean = false;

    constructor(props: IRootProviderProps<SagaContext, State>, context: IVssComponentContext) {
        super(props, context);
        const { initialState, sagaContext, modules, scenarioName, ttiSplitTimingName, contentViewObserverModuleArgs, useStoreService, serviceInstanceType } = this.props;

        const allModules: IModule<any>[] = [
            NavigationModule,
            getTelemetryModule(scenarioName, serviceInstanceType),
            getTTIMonitorModule(ttiSplitTimingName, serviceInstanceType),
            VSSPageDataModule,
            QueuedActionsModule
        ];

        if (contentViewObserverModuleArgs) {
            allModules.push(getContentViewObserverModule(contentViewObserverModuleArgs));
        }

        allModules.push(...modules);

        this._store = createStore({
                initialState: initialState,
                enhancers: [],
                extensions: [getSagaExtension(sagaContext, this._onSagaError)],
            }, ...allModules);

        if (useStoreService) {
            const storeService = context.pageContext.getService<IReduxStoreService>("redux-store-service");
            storeService.setStore(this._store);
        }
    }

    public render() {
        const { postTTIModules } = this.props;

        this._throwIfSagaFailed();

        return (
            <Provider store={this._store!}>
                <>
                    {this.props.children}
                    {postTTIModules && postTTIModules.length > 0 && (
                        <PostTTI>
                            {postTTIModules.map(postTTIModule => (
                                <WrappedModule
                                    key={postTTIModule.reduxModuleId}
                                    contributionId={postTTIModule.contributionId}
                                    reduxModuleId={postTTIModule.reduxModuleId}
                                />
                            ))}
                        </PostTTI>
                    )}
                </>
            </Provider>
        );
    }

    public componentWillUnmount() {
        if (this._store) {
            this._store.dispose();
        }
        this._store = undefined;
    }

    public componentDidMount() {
        this._mounted = true;
        this._throwIfSagaFailed();
    }

    public componentDidUpdate() {
        this._throwIfSagaFailed();
    }

    /**
     * When the saga middleware gets an unhandled exception,
     * assign it to the state so it can be thrown in the React context
     */
    private _onSagaError = (caughtSagaError: Error): void => {
        this._caughtSagaError = caughtSagaError;
        if (this._mounted) {
            this.forceUpdate();
        }
    };

    private _throwIfSagaFailed(): void {
        // If there was a caught error, throw it now
        if (this._caughtSagaError) {
            throw this._caughtSagaError;
        }
    }
}
